import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, ArrowLeft, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { mockValues, saveProfile, loadProfile } from '../mock';
import { saveProfileDraft } from '../services/api';

const ValuesProfile = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedValues, setSelectedValues] = useState([]);
  const [personalValues, setPersonalValues] = useState([]);
  const [capacity, setCapacity] = useState({ time: 1, money: 0, attention: 1 });
  const [goalText, setGoalText] = useState('');
  const [showGoalExamples, setShowGoalExamples] = useState(false);
  const [avoidTopics, setAvoidTopics] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const existing = loadProfile();
    if (existing) {
      setSelectedValues(existing.selectedValues || []);
      setPersonalValues(existing.personalValues || []);
      setCapacity(existing.capacity || { time: 1, money: 0, attention: 1 });
      setGoalText(existing.goalText || '');
      setAvoidTopics(existing.boundaries?.avoidTopics || []);
    }
  }, []);

  const handleValueToggle = (valueId) => {
    setSelectedValues(prev =>
      prev.includes(valueId)
        ? prev.filter(v => v !== valueId)
        : [...prev, valueId]
    );
  };

  const handlePersonalToggle = (valueId) => {
    setPersonalValues(prev =>
      prev.includes(valueId)
        ? prev.filter(v => v !== valueId)
        : [...prev, valueId]
    );
  };

  const handleTopicToggle = (topic) => {
    setAvoidTopics(prev =>
      prev.includes(topic)
        ? prev.filter(t => t !== topic)
        : [...prev, topic]
    );
  };

  const handleNext = () => {
    if (step < 6) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleComplete = async () => {
    setIsSaving(true);
    
    const profile = {
      selectedValues,
      personalValues,
      capacity: {
        time: ['none', '5-10 min', '30 min', 'more'][capacity.time],
        money: ['none', '$1-5', '$10-25', 'more'][capacity.money],
        attention: ['low', 'medium', 'high'][capacity.attention]
      },
      goalText,
      boundaries: { avoidTopics },
      completedAt: new Date().toISOString()
    };
    
    // Save locally first
    saveProfile(profile);
    
    // Try to save to backend API
    try {
      await saveProfileDraft(profile);
    } catch (err) {
      console.error('Error saving to API:', err);
      // Continue anyway - local storage has the data
    }
    
    setIsSaving(false);
    navigate('/dashboard');
  };

  const timeLabels = ['None', '5-10 min', '30 min', 'More'];
  const moneyLabels = ['None', '$1-5', '$10-25', 'More'];
  const attentionLabels = ['Low', 'Medium', 'High'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern">
      {/* Header */}
      <header className="border-b border-calm-border bg-fern-card/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-5">
          <div 
            className="text-2xl font-bold text-calm-text cursor-pointer" 
            style={{fontFamily: 'DM Serif Display, serif'}}
            onClick={() => navigate('/')}
          >
            OHMNAI
          </div>
        </div>
      </header>

      {/* Progress indicator */}
      <div className="max-w-2xl mx-auto px-6 pt-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          {[1, 2, 3, 4, 5, 6].map((s) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-all ${
                s === step ? 'bg-forest w-4' : s < step ? 'bg-herbal' : 'bg-calm-border'
              }`}
            />
          ))}
        </div>
        <p className="text-center text-sm text-calm-text-secondary">
          Step {step} of 6
        </p>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Step 1: Values Selection */}
        {step === 1 && (
          <div className="space-y-8 fade-in-up">
            <div className="space-y-4">
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                When you imagine a world you'd be proud to live in—what matters?
              </h1>
              <p className="text-calm-text-secondary">Select all that resonate. There's no ranking here.</p>
            </div>

            <div className="space-y-3">
              {mockValues.map((value) => (
                <div
                  key={value.id}
                  className={`bg-fern-card rounded-xl p-6 border transition-all cursor-pointer ${
                    selectedValues.includes(value.id) 
                      ? 'border-forest bg-herbal-light/50' 
                      : 'border-calm-border hover:border-herbal'
                  }`}
                  onClick={() => handleValueToggle(value.id)}
                >
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selectedValues.includes(value.id)}
                      onCheckedChange={() => handleValueToggle(value.id)}
                      className="mt-1 border-calm-border data-[state=checked]:bg-forest data-[state=checked]:border-forest"
                    />
                    <div className="flex-1">
                      <Label className="text-lg font-medium text-calm-text cursor-pointer">
                        {value.label}
                      </Label>
                      <p className="text-calm-text-secondary text-sm mt-1">{value.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-6">
              <Button
                onClick={handleNext}
                disabled={selectedValues.length === 0}
                className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-xl transition-colors disabled:opacity-50"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Resonance Check */}
        {step === 2 && (
          <div className="space-y-8 fade-in-up">
            <div className="space-y-4">
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                Which of these feel personal—not abstract?
              </h1>
              <p className="text-calm-text-secondary">Not what you "should" care about. What you actually do.</p>
            </div>

            <div className="space-y-3">
              {mockValues
                .filter(v => selectedValues.includes(v.id))
                .map((value) => (
                  <div
                    key={value.id}
                    className={`bg-fern-card rounded-xl p-6 border transition-all cursor-pointer ${
                      personalValues.includes(value.id) 
                        ? 'border-forest bg-herbal-light/50' 
                        : 'border-calm-border hover:border-herbal'
                    }`}
                    onClick={() => handlePersonalToggle(value.id)}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={personalValues.includes(value.id)}
                        onCheckedChange={() => handlePersonalToggle(value.id)}
                        className="mt-1 border-calm-border data-[state=checked]:bg-forest data-[state=checked]:border-forest"
                      />
                      <div className="flex-1">
                        <Label className="text-lg font-medium text-calm-text cursor-pointer">
                          {value.label}
                        </Label>
                        <p className="text-calm-text-secondary text-sm mt-1">{value.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="flex justify-between pt-6">
              <Button
                onClick={handleBack}
                variant="outline"
                className="border-calm-border text-forest hover:bg-fern px-8 py-6 text-base rounded-xl transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={personalValues.length === 0}
                className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-xl transition-colors disabled:opacity-50"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Capacity Check */}
        {step === 3 && (
          <div className="space-y-8 fade-in-up">
            <div className="space-y-4">
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                What do you realistically have right now?
              </h1>
              <p className="text-calm-text-secondary">Low capacity is not failure. It's honest.</p>
            </div>

            <div className="bg-fern-card rounded-xl p-8 border border-calm-border space-y-8">
              {/* Time */}
              <div className="space-y-4">
                <Label className="text-lg font-medium text-calm-text">Time</Label>
                <Slider
                  value={[capacity.time]}
                  onValueChange={(value) => setCapacity({ ...capacity, time: value[0] })}
                  max={3}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-calm-text-secondary">
                  {timeLabels.map((label, i) => (
                    <span key={i} className={capacity.time === i ? 'font-medium text-forest' : ''}>
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Money */}
              <div className="space-y-4">
                <Label className="text-lg font-medium text-calm-text">Money</Label>
                <Slider
                  value={[capacity.money]}
                  onValueChange={(value) => setCapacity({ ...capacity, money: value[0] })}
                  max={3}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-calm-text-secondary">
                  {moneyLabels.map((label, i) => (
                    <span key={i} className={capacity.money === i ? 'font-medium text-forest' : ''}>
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Attention */}
              <div className="space-y-4">
                <Label className="text-lg font-medium text-calm-text">Attention</Label>
                <Slider
                  value={[capacity.attention]}
                  onValueChange={(value) => setCapacity({ ...capacity, attention: value[0] })}
                  max={2}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-calm-text-secondary">
                  {attentionLabels.map((label, i) => (
                    <span key={i} className={capacity.attention === i ? 'font-medium text-forest' : ''}>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <Button
                onClick={handleBack}
                variant="outline"
                className="border-calm-border text-forest hover:bg-fern px-8 py-6 text-base rounded-xl transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={handleNext}
                className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-xl transition-colors"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Your Goal (Optional) */}
        {step === 4 && (
          <div className="space-y-8 fade-in-up">
            <div className="space-y-4">
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                Your current goal (optional)
              </h1>
              <p className="text-lg text-calm-text-secondary">
                If OHMNAI could help you move one thing forward right now, what would it be?
              </p>
              <p className="text-sm text-calm-text-secondary/70">
                One sentence is enough. This can be messy. You can skip.
              </p>
            </div>

            <div className="bg-fern-card rounded-xl p-8 border border-calm-border">
              <Textarea
                value={goalText}
                onChange={(e) => setGoalText(e.target.value)}
                placeholder="Type your goal here..."
                className="min-h-[120px] text-base resize-none bg-fern-light border-calm-border focus:border-forest focus:ring-forest/20 text-calm-text placeholder:text-calm-text-secondary/50"
                maxLength={500}
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-calm-text-secondary/70">
                  {goalText.length > 0 && `${goalText.length} characters`}
                </span>
                {goalText.length >= 160 && goalText.length <= 300 && (
                  <span className="text-xs text-sage-icon">Good length</span>
                )}
              </div>
            </div>

            {/* Optional Examples */}
            <div>
              <button
                onClick={() => setShowGoalExamples(!showGoalExamples)}
                className="flex items-center gap-2 text-sm text-calm-text-secondary hover:text-forest transition-colors"
              >
                {showGoalExamples ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Hide ideas
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Need ideas?
                  </>
                )}
              </button>

              {showGoalExamples && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm text-calm-text-secondary mb-3">Examples to get you started:</p>
                  {[
                    "Get back into a steady sleep routine.",
                    "Make one concrete step on something I've been avoiding.",
                    "Support something I care about without doomscrolling."
                  ].map((example, i) => (
                    <div
                      key={i}
                      className="bg-herbal-light/50 rounded-lg p-4 text-sm text-forest border border-herbal"
                    >
                      "{example}"
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-between pt-6">
              <Button
                onClick={handleBack}
                variant="outline"
                className="border-calm-border text-forest hover:bg-fern px-8 py-6 text-base rounded-xl transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={handleNext}
                className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-xl transition-colors"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Boundaries (Optional) */}
        {step === 5 && (
          <div className="space-y-8 fade-in-up">
            <div className="space-y-4">
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                Are there topics you'd prefer to avoid?
              </h1>
              <p className="text-calm-text-secondary">This is optional. Skip if nothing comes to mind.</p>
            </div>

            <div className="space-y-3">
              {['Politics', 'Religion', 'Graphic content', 'Urgent language', 'Fundraising'].map((topic) => (
                <div
                  key={topic}
                  className={`bg-fern-card rounded-xl p-6 border transition-all cursor-pointer ${
                    avoidTopics.includes(topic) 
                      ? 'border-forest bg-herbal-light/50' 
                      : 'border-calm-border hover:border-herbal'
                  }`}
                  onClick={() => handleTopicToggle(topic)}
                >
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={avoidTopics.includes(topic)}
                      onCheckedChange={() => handleTopicToggle(topic)}
                      className="border-calm-border data-[state=checked]:bg-forest data-[state=checked]:border-forest"
                    />
                    <Label className="text-lg font-medium text-calm-text cursor-pointer">
                      {topic}
                    </Label>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between pt-6">
              <Button
                onClick={handleBack}
                variant="outline"
                className="border-calm-border text-forest hover:bg-fern px-8 py-6 text-base rounded-xl transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={handleNext}
                className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-xl transition-colors"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 6: Reflection */}
        {step === 6 && (
          <div className="space-y-8 fade-in-up">
            <div className="space-y-4">
              <h1 className="text-3xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                Here's what you've shared
              </h1>
              <p className="text-calm-text-secondary">Take a moment to review. You can go back and adjust anything.</p>
            </div>

            <div className="bg-fern-card rounded-xl p-8 border border-calm-border space-y-6">
              <div>
                <h3 className="font-medium text-calm-text mb-3">Your values</h3>
                <div className="flex flex-wrap gap-2">
                  {personalValues.map(valueId => {
                    const value = mockValues.find(v => v.id === valueId);
                    return (
                      <span key={valueId} className="px-4 py-2 bg-herbal-light text-forest rounded-full text-sm border border-herbal">
                        {value?.label}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-calm-text mb-3">Your capacity</h3>
                <div className="space-y-2 text-calm-text-secondary">
                  <p>Time: <span className="font-medium text-forest">{timeLabels[capacity.time]}</span></p>
                  <p>Money: <span className="font-medium text-forest">{moneyLabels[capacity.money]}</span></p>
                  <p>Attention: <span className="font-medium text-forest">{attentionLabels[capacity.attention]}</span></p>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-calm-text mb-3">Your current goal</h3>
                {goalText ? (
                  <div className="bg-herbal-light/50 rounded-lg p-4 border border-herbal">
                    <p className="text-forest leading-relaxed">"{goalText}"</p>
                  </div>
                ) : (
                  <p className="text-calm-text-secondary/70 italic">(not specified)</p>
                )}
              </div>

              {avoidTopics.length > 0 && (
                <div>
                  <h3 className="font-medium text-calm-text mb-3">Topics to avoid</h3>
                  <div className="flex flex-wrap gap-2">
                    {avoidTopics.map(topic => (
                      <span key={topic} className="px-4 py-2 bg-fern text-calm-text-secondary rounded-full text-sm border border-calm-border">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-6">
              <Button
                onClick={handleBack}
                variant="outline"
                className="border-calm-border text-forest hover:bg-fern px-8 py-6 text-base rounded-xl transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={handleComplete}
                disabled={isSaving}
                className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-xl transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Complete profile <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValuesProfile;
